/* Notion-style inline edit primitive.
   - Click value → becomes input
   - Blur or Enter → commits via onSave(newValue)
   - Escape → reverts
   - After successful save, "Saved ✓" badge fades in for 2s
   - Multiline variant uses <textarea>
*/
import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

export default function InlineEdit({
 value,
 onSave,
 multiline = false,
 placeholder = "Click to edit",
 className = "",
 inputClassName = "",
 prefix = "",
 testid,
}) {
 const [local, setLocal] = useState(value ?? "");
 const [editing, setEditing] = useState(false);
 const [savedAt, setSavedAt] = useState(0);
 const [savingErr, setSavingErr] = useState("");
 const inputRef = useRef(null);

 // Sync external value down when not editing
 useEffect(() => { if (!editing) setLocal(value ?? ""); }, [value, editing]);

 // Auto-fade Saved indicator
 useEffect(() => {
  if (!savedAt) return;
  const t = setTimeout(() => setSavedAt(0), 2200);
  return () => clearTimeout(t);
 }, [savedAt]);

 const commit = async () => {
  setEditing(false);
  if ((local ?? "") === (value ?? "")) return;
  try {
   await onSave(local);
   setSavedAt(Date.now());
   setSavingErr("");
  } catch (e) {
   setSavingErr(e.message || "Save failed");
   setLocal(value ?? ""); // revert on failure
  }
 };

 const cancel = () => { setLocal(value ?? ""); setEditing(false); };

 if (editing) {
  const Tag = multiline ? "textarea" : "input";
  return (
   <span className={`relative inline-flex items-center gap-2 ${className}`}>
    <Tag
     ref={inputRef}
     autoFocus
     value={local}
     onChange={(e) => setLocal(e.target.value)}
     onBlur={commit}
     onKeyDown={(e) => {
      if (!multiline && e.key === "Enter") { e.preventDefault(); e.target.blur(); }
      if (e.key === "Escape") { e.preventDefault(); cancel(); }
     }}
     rows={multiline ? 2 : undefined}
     placeholder={placeholder}
     className={`bg-white/[0.08] border border-amber-300/50 rounded-lg px-2 py-1 outline-none text-white focus:ring-2 focus:ring-amber-300/40 min-w-[8rem] ${multiline ? "w-full resize-y" : ""} ${inputClassName}`}
     data-testid={testid ? `${testid}-input` : undefined}
    />
   </span>
  );
 }

 return (
  <span className={`group inline-flex items-center gap-2 ${className}`}>
   <span
    onClick={() => setEditing(true)}
    className="cursor-text rounded px-1 -mx-1 hover:bg-amber-200/10 transition-colors break-words"
    data-testid={testid}
   >
    {prefix}
    {local || <span className="text-white/30 italic">{placeholder}</span>}
   </span>
   {savedAt ? (
    <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px] uppercase tracking-wider font-black animate-fade-in" data-testid={testid ? `${testid}-saved` : undefined}>
     <Check className="w-3 h-3" /> Saved
    </span>
   ) : null}
   {savingErr ? (
    <span className="text-rose-300 text-[10px] uppercase tracking-wider font-black" title={savingErr}>! Failed</span>
   ) : null}
  </span>
 );
}
